import { useState, ReactNode, useEffect } from 'react';
import { AppContext } from './contexts';
import { Notification, LoginResponse, CorporateLoginResponse } from '@/types';
import localforage from 'localforage';
//import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '@/components/ui/LoadingOverlay';
import { SyncSessionService } from '@/services/syncSessionService';
import { encodeToken } from '@/utils/tokenUtils';
interface AppProviderProps {
    children: ReactNode;
}

function parseStoredJson<T>(key: string, fallback: T): T {
    try {
        const stored = localStorage.getItem(key);
        return stored ? (JSON.parse(stored) as T) : fallback;
    } catch (error) {
        console.error(`Failed to parse ${key}:`, error);
        return fallback;
    }
}

export default function AppProvider({ children }: AppProviderProps) {
    const [notification, setNotification] = useState<Notification>({
        message: null,
        severity: 'info',
    });

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [manageStoreId, setManageStoreId] = useState<number | null>(() => {
        const stored = localStorage.getItem('manageStoreId');
        if (!stored) return null;
        const parsed = Number(stored);
        return Number.isNaN(parsed) ? null : parsed;
    });
    const [manageStoreName, setManageStoreName] = useState<string | null>(null);

    const [userDetails, setUserDetails] = useState<LoginResponse | null>(() =>
        parseStoredJson<LoginResponse | null>('userDetails', null),
    );

    const [corporationDetails, setCorporationDetails] = useState<CorporateLoginResponse>(
        () => parseStoredJson<CorporateLoginResponse | null>('corporationDetails', null) as CorporateLoginResponse,
    );

    const [userPermission, setUserPermission] = useState(
        () => parseStoredJson<CorporateLoginResponse | null>('corporationDetails', null)?.user_abilities || [],
    );
    const [redirectTo, setRedirectTo] = useState('');

    useEffect(() => {
        const unsubscribe = SyncSessionService.subscribe(async ({ type, data }) => {
            switch (type) {
                case 'switch': {
                    const userDetails = JSON.parse(localStorage.getItem('userDetails') || 'null');
                    if (!data || !userDetails) return;
                    if (data.token === corporationDetails?.token) return;

                    setCorporationDetails(data);
                    setUserDetails(userDetails);
                    setUserPermission(data.user_abilities ?? []);

                    setManageStoreId(null);
                    setManageStoreName(null);
                    setNotification({ message: null });
                    setRedirectTo('dashboard');
                    break;
                }
                case 'change': {
                    if (data.token === corporationDetails?.token) return;
                    setCorporationDetails(data);
                    setNotification({ message: null });
                    setRedirectTo('corporation-selection');
                    break;
                }
                case 'login': {
                    const { token, ...rest } = data;
                    await localforage.setItem('login_token', encodeToken(data.token));
                    await localforage.setItem('hasLoginSession', true);
                    await localforage.setItem('login_data', rest);
                    setUserDetails(data);

                    localStorage.setItem('userDetails', JSON.stringify(data));
                    localStorage.setItem('isUserLogin', 'isUserLogin');
                    setNotification({ message: null });
                    setRedirectTo('corporation');
                    break;
                }

                case 'logout': {
                    setManageStoreId(null);
                    setUserDetails({
                        token: '',
                        username: 'Guest',
                        user_level: 0,
                        user_abilities: [],
                        token_expires: null,
                    });
                    setCorporationDetails({
                        token: '',
                        username: 'Guest',
                        corporate_name: '',
                        user_level: 0,
                        user_abilities: [],
                        token_expires: null,
                    });
                    setNotification({ message: null });
                    setRedirectTo('login');
                    break;
                }
            }
        });

        return unsubscribe;
    }, [
        corporationDetails,
        userDetails,
        setCorporationDetails,
        setUserPermission,
        setManageStoreId,
        setManageStoreName,
    ]);

    // Load user details from localStorage on app start
    useEffect(() => {
        const storedUser = parseStoredJson<LoginResponse | null>('userDetails', null);
        if (storedUser) {
            setUserDetails(storedUser);
        }
    }, []);

    // Save userDetails to localStorage when it changes
    useEffect(() => {
        if (userDetails) {
            localStorage.setItem('userDetails', JSON.stringify(userDetails));
        }
    }, [userDetails]);

    // Persist store selection across reloads
    useEffect(() => {
        if (manageStoreId != null) {
            localStorage.setItem('manageStoreId', String(manageStoreId));
        } else {
            localStorage.removeItem('manageStoreId');
        }
    }, [manageStoreId]);

    // Logout function
    // const logoutUser = async () => {
    //     await localforage.clear();
    //     localStorage.removeItem('userDetails');
    //     localStorage.removeItem('tokenExpires');
    //     setUserDetails(null);

    //     if (window.location.pathname !== '/login') {
    //         window.location.href = '/login';
    //     }
    // };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const token = await localforage.getItem<string>('login_token');
                const storedUserDetails = parseStoredJson<LoginResponse | null>('userDetails', null);
                const storedCorporationDetails = parseStoredJson<CorporateLoginResponse | null>(
                    'corporationDetails',
                    null,
                );
                const storedPermissions = storedCorporationDetails?.user_abilities || null;

                if (token && storedUserDetails) {
                    setUserDetails(storedUserDetails);
                }
                if (storedCorporationDetails) {
                    setCorporationDetails(storedCorporationDetails);
                }
                if (storedPermissions) {
                    setUserPermission(storedPermissions);
                }
            } catch (error) {
                console.error('Failed to fetch initial data in AppProvider:', error);
            }
        };

        fetchInitialData();
    }, []);

    return (
        <AppContext.Provider
            value={{
                notification,
                setNotification,
                isDialogOpen,
                setIsDialogOpen,
                manageStoreId,
                setManageStoreId,
                manageStoreName,
                setManageStoreName,
                userDetails,
                setUserDetails,
                corporationDetails,
                setCorporationDetails,
                userPermission,
                setUserPermission,
                redirectTo,
                setRedirectTo,
            }}
        >
            <LoadingOverlay />
            {children}
        </AppContext.Provider>
    );
}
